/**
 * Sound helper using Web Audio API — no external files.
 * chime()  — pleasant two-tone bell for new notifications
 * reminder() — three-tone alert for daily-log reminders
 */
let _ctx = null;
function ctx() {
  if (_ctx) return _ctx;
  const C = window.AudioContext || window.webkitAudioContext;
  if (!C) return null;
  _ctx = new C();
  return _ctx;
}

function beep(freq, when, dur = 0.18, gain = 0.15, wave = "sine") {
  const a = ctx();
  if (!a) return;
  const o = a.createOscillator();
  const g = a.createGain();
  o.type = wave;
  o.frequency.setValueAtTime(freq, a.currentTime + when);
  g.gain.setValueAtTime(0.0001, a.currentTime + when);
  g.gain.exponentialRampToValueAtTime(gain, a.currentTime + when + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, a.currentTime + when + dur);
  o.connect(g); g.connect(a.destination);
  o.start(a.currentTime + when);
  o.stop(a.currentTime + when + dur + 0.02);
}

export function chime() {
  try {
    // pleasant "message received" — C6 -> E6
    beep(1046.5, 0, 0.14, 0.14, "sine");
    beep(1318.5, 0.12, 0.20, 0.14, "sine");
  } catch (e) { /* audio may be blocked before user gesture */ }
}

// Distinct, attention-grabbing sound for @mentions
export function mentionChime() {
  try {
    // 3-note ascending "you got tagged" — A5 -> C#6 -> E6
    beep(880.0, 0.00, 0.10, 0.20, "triangle");
    beep(1108.7, 0.11, 0.10, 0.20, "triangle");
    beep(1318.5, 0.22, 0.24, 0.22, "triangle");
  } catch (e) { /* silent */ }
}

// Warm bell-like G5 → D6 pair for the Monday weekly-report flow.
export function weeklyReportChime() {
  try {
    beep(783.99, 0.00, 0.22, 0.18, "sine");   // G5
    beep(1174.66, 0.24, 0.32, 0.18, "sine");  // D6
  } catch (e) { /* silent */ }
}

// ---------- Continuous alarm buzzer ----------
// Two-tone repeating siren (~2 s per cycle) that loops until stop() is called.
// Used by the AlarmOverlay for meeting/reminder alarms — plays on every
// assignee's device the moment the alarm fires, and only stops when they hit
// the STOP button (or snooze it forward).
let _alarmCtx = null;
let _alarmOsc = null;
let _alarmGain = null;

export function startAlarm() {
  try {
    stopAlarm();   // idempotent — safe to call twice
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    _alarmCtx = new Ctx();
    _alarmOsc = _alarmCtx.createOscillator();
    _alarmGain = _alarmCtx.createGain();
    // Sine wave = warm, mellow — MUCH softer than the previous square-wave buzz.
    // Two-note pleasant chime (C5 → E5, a major-third) at low volume, with
    // long gentle fades so it never startles even when playing over speakers.
    _alarmOsc.type = "sine";
    _alarmOsc.frequency.setValueAtTime(523.25, _alarmCtx.currentTime);
    _alarmGain.gain.setValueAtTime(0.0001, _alarmCtx.currentTime);
    _alarmOsc.connect(_alarmGain).connect(_alarmCtx.destination);
    // Chime cycle: ring — pause — ring — pause. ~2.4 s per cycle. 400 cycles ≈ 16 min ceiling.
    const PEAK = 0.09;                        // ↓ down from 0.28 (~3× quieter)
    const NOTE = 0.55;                        // ↑ longer, softer note (was 0.32 clipped)
    const GAP  = 0.65;                        // ↑ breathing room between rings
    let t = _alarmCtx.currentTime + 0.05;
    for (let i = 0; i < 400; i++) {
      const freq = i % 2 === 0 ? 523.25 : 659.25;   // C5 → E5, a warm major third
      _alarmOsc.frequency.setValueAtTime(freq, t);
      // Slow attack & release so we get a bell-like envelope, not a click.
      _alarmGain.gain.setValueAtTime(0.0001, t);
      _alarmGain.gain.exponentialRampToValueAtTime(PEAK, t + 0.10);
      _alarmGain.gain.exponentialRampToValueAtTime(0.0001, t + NOTE);
      t += NOTE + GAP;
    }
    _alarmOsc.start();
  } catch (e) { /* silent */ }
}

export function stopAlarm() {
  try {
    if (_alarmOsc) { _alarmOsc.stop(); _alarmOsc.disconnect(); _alarmOsc = null; }
    if (_alarmGain) { _alarmGain.disconnect(); _alarmGain = null; }
    if (_alarmCtx) { _alarmCtx.close(); _alarmCtx = null; }
  } catch (e) { /* silent */ }
}

export function reminder() {
  try {
    // 3 quick notes — reminder alert
    beep(880, 0.0, 0.14, 0.16, "triangle");
    beep(880, 0.18, 0.14, 0.16, "triangle");
    beep(1174.7, 0.36, 0.22, 0.18, "triangle");
  } catch (e) { /* silent */ }
}

// call once on the first user interaction to unlock audio (Chrome autoplay policy)
export function unlockAudio() {
  const a = ctx();
  if (!a) return;
  if (a.state === "suspended") a.resume().catch(() => {});
}
